import React from 'react';
import { Instagram, Youtube, Facebook, Linkedin, Twitter, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const iconMap = {
  instagram: Instagram,
  youtube: Youtube,
  facebook: Facebook,
  linkedin: Linkedin,
  twitter: Twitter,
  whatsapp: MessageCircle,
};

const linkStyle = {
  fontFamily: 'DM Sans, sans-serif',
  fontSize: '0.78rem',
  fontWeight: 300,
  lineHeight: 2.1,
};

const headingStyle = {
  fontFamily: 'DM Sans, sans-serif',
  fontSize: '0.6rem',
  letterSpacing: '0.24em',
  textTransform: 'uppercase',
  fontWeight: 500,
};

const Footer = ({ data }) => {
  const { language, localizedPath } = useLanguage();
  const t = translations[language];
  const socialMedia = data?.socialMedia || [];
  const companyName = data?.companyName || 'Your Broker Portugal';
  const year = new Date().getFullYear();

  const description =
    data?.footerDescription ||
    (language === 'en'
      ? 'Private buying agent for international buyers in Portugal.'
      : 'Consultoria privada para compradores internacionais em Portugal.');

  const navItems = [
    { route: localizedPath('/advisory'), label: t.advisory },
    { route: localizedPath('/services'), label: t.services },
    { route: localizedPath('/regions'), label: t.regions },
    { route: localizedPath('/journal'), label: t.journal },
    { route: localizedPath('/international'), label: t.international },
    { route: localizedPath('/contact'), label: t.contact },
  ];

  return (
    <footer
      className="bg-editorial-charcoal text-editorial-bone"
      data-testid="editorial-footer"
    >
      {/* Top hairline */}
      <div className="h-px bg-editorial-bone/8" />

      <div className="px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-24">
        <div className="max-w-7xl mx-auto">
          {/* Top grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10">
            {/* Brand */}
            <div className="md:col-span-5">
              <p className="text-editorial-bronze-lt mb-5" style={headingStyle}>
                {t.privateAdvisory}
              </p>
              <h3
                className="text-editorial-bone font-light leading-tight mb-6"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.85rem' }}
              >
                {companyName}
              </h3>
              <p
                className="text-editorial-bone/55 max-w-md"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', fontWeight: 300, lineHeight: 1.9 }}
              >
                {description}
              </p>

              {/* Contact */}
              <div className="mt-10 space-y-2">
                <a
                  href="mailto:portugal@yourbrokerportugal.com"
                  className="block text-editorial-bone/65 hover:text-editorial-bronze-lt transition-colors border-b border-editorial-bone/10 hover:border-editorial-bronze-lt pb-1 inline-block"
                  style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', fontWeight: 300 }}
                  data-testid="footer-email"
                >
                  portugal@yourbrokerportugal.com
                </a>
                <br />
                <a
                  href="tel:+351931997415"
                  className="block text-editorial-bone/65 hover:text-editorial-bronze-lt transition-colors mt-2"
                  style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', fontWeight: 300 }}
                  data-testid="footer-phone"
                >
                  +351 931 997 415
                </a>
              </div>
            </div>

            {/* Spacer for desktop */}
            <div className="hidden md:block md:col-span-1" />

            {/* Navigation */}
            <div className="md:col-span-3">
              <p className="text-editorial-bronze-lt mb-5" style={headingStyle}>
                {t.quickLinks}
              </p>
              <ul>
                {navItems.map((item) => (
                  <li key={item.route}>
                    <Link
                      to={item.route}
                      className="text-editorial-bone/65 hover:text-editorial-bone transition-colors"
                      style={linkStyle}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Social */}
            <div className="md:col-span-3">
              <p className="text-editorial-bronze-lt mb-5" style={headingStyle}>
                {t.socialMedia}
              </p>
              {socialMedia.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {socialMedia.map((social) => {
                    const Icon = iconMap[social.platform] || Instagram;
                    return (
                      <a
                        key={social.id}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 border border-editorial-bone/15 hover:border-editorial-bronze-lt hover:bg-editorial-bone/5 text-editorial-bone/70 hover:text-editorial-bronze-lt flex items-center justify-center transition-all duration-300"
                        title={social.platform}
                        data-testid={`footer-social-${social.platform}`}
                      >
                        <Icon className="w-4 h-4" strokeWidth={1.4} />
                      </a>
                    );
                  })}
                </div>
              ) : (
                <p className="text-editorial-bone/40" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', fontWeight: 300 }}>
                  —
                </p>
              )}

              {/* AMI / NIPC */}
              <p
                className="mt-10 text-editorial-bone/35"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.7rem', letterSpacing: '0.05em', fontWeight: 300, lineHeight: 1.9 }}
              >
                AMI 19715
                <br />
                NIPC 516 549 642
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-editorial-bone/8 mt-16 md:mt-20 mb-8" />

          {/* Bottom bar */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <p
              className="text-editorial-bone/35"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', fontWeight: 300 }}
            >
              © {year} {companyName}. {t.allRightsReserved}.
            </p>
            <div className="flex flex-wrap gap-8">
              <Link
                to={localizedPath('/privacy')}
                className="text-editorial-bone/45 hover:text-editorial-bronze-lt transition-colors"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 400 }}
              >
                {t.privacyPolicy}
              </Link>
              <a
                href="https://www.cniacf.pt/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-editorial-bone/45 hover:text-editorial-bronze-lt transition-colors"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 400 }}
              >
                {t.arbitrationCenter}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
